import {
  PlusIcon,
  Squares2X2Icon,
  ListBulletIcon,
  FunnelIcon,
  CalendarDaysIcon,
  LightBulbIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  InboxArrowDownIcon,
} from "@heroicons/react/20/solid";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { BUTTON_CLASSES, ICON_CLASSES, VIEW_MODES } from "./constants";

// Events Header Component
export const EventsHeader = ({ viewMode, setViewMode, setShowFilterModal, setShowHealthModal, navigate, isListTab, isSuperAdmin }) => (
  <div className="flex mt-4 sm:mt-0 justify-between items-center">
    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Events</h1>
    {isListTab && (
      <div className="flex items-center gap-3">
        <ViewToggleButton viewMode={viewMode} setViewMode={setViewMode} />
        <FilterButton setShowFilterModal={setShowFilterModal} />
        {isSuperAdmin && <HealthCheckButton onClick={() => setShowHealthModal(true)} />}
        <CreateEventButton navigate={navigate} />
      </div>
    )}
  </div>
);

// Health Check Button Component
const HealthCheckButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={BUTTON_CLASSES.SECONDARY}
    title="Run event health check"
  >
    <ShieldCheckIcon className={`-ml-0.5 ${ICON_CLASSES.MEDIUM}`} aria-hidden="true" />
    <span className="hidden sm:inline-block">Health Check</span>
  </button>
);

// View Toggle Button Component
const ViewToggleButton = ({ viewMode, setViewMode }) => (
  <button
    type="button"
    onClick={() => setViewMode(viewMode === VIEW_MODES.CARD ? VIEW_MODES.TABLE : VIEW_MODES.CARD)}
    className={BUTTON_CLASSES.SECONDARY}
  >
    <Squares2X2Icon className={`-ml-0.5 ${ICON_CLASSES.MEDIUM} ${viewMode === VIEW_MODES.CARD ? 'text-primary' : 'text-gray-400 dark:text-gray-500'} hidden sm:block`} aria-hidden="true" />
    <ListBulletIcon className={`${ICON_CLASSES.MEDIUM} ${viewMode === VIEW_MODES.TABLE ? 'text-primary' : 'text-gray-400 dark:text-gray-500'} hidden sm:block`} aria-hidden="true" />
    <div className="sm:hidden">
      {viewMode === VIEW_MODES.CARD ? (
        <Squares2X2Icon className={`-ml-0.5 ${ICON_CLASSES.MEDIUM} text-primary`} aria-hidden="true" />
      ) : (
        <ListBulletIcon className={`${ICON_CLASSES.MEDIUM} text-primary`} aria-hidden="true" />
      )}
    </div>
  </button>
);

// Filter Button Component
const FilterButton = ({ setShowFilterModal }) => (
  <button
    type="button"
    onClick={() => setShowFilterModal(true)}
    className={BUTTON_CLASSES.SECONDARY}
  >
    <FunnelIcon className={`-ml-0.5 ${ICON_CLASSES.MEDIUM}`} aria-hidden="true" />
    <span className="hidden sm:inline-block">Filter</span>
  </button>
);

// Create Event Button Component
export const CreateEventButton = ({ navigate }) => (
  <button
    type="button"
    onClick={() => navigate("/events/new")}
    className={BUTTON_CLASSES.PRIMARY}
  >
    <PlusIcon className={`-ml-0.5 ${ICON_CLASSES.MEDIUM}`} aria-hidden="true" />
    Create <span className="hidden sm:inline-block"> event</span>
  </button>
);

// Tab Count Badge
const TabCountBadge = ({ count, isActive }) => {
  if (count == null) return null;
  return (
    <span
      className={`ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs font-medium ${isActive ? "bg-primary text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
        }`}
    >
      {count}
    </span>
  );
};

// Events Tab Button
const EventsTabButton = ({ label, tabKey, activeTab, onClick, count, icon: Icon }) => {
  const isActive = activeTab === tabKey;
  return (
    <button
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center whitespace-nowrap flex-shrink-0 ${isActive
        ? "border-primary text-primary"
        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600"
        }`}
      onClick={onClick}
    >
      {Icon && (
        <Icon
          className={`mr-1.5 ${ICON_CLASSES.SMALL} ${isActive ? "text-primary" : "text-gray-400 dark:text-gray-500"}`}
          aria-hidden="true"
        />
      )}
      {label}
      <TabCountBadge count={count} isActive={isActive} />
    </button>
  );
};

// Events Tabs Component
export const EventsTabs = ({ activeTab, navigate, isSuperAdmin, counts }) => (
  <div className="mt-4 flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
    <EventsTabButton label="Upcoming" tabKey="upcoming" activeTab={activeTab} onClick={() => navigate("/events")} count={counts.upcoming} icon={CalendarDaysIcon} />
    <EventsTabButton label="Ideas" tabKey="suggestions" activeTab={activeTab} onClick={() => navigate("/events/suggestions")} count={counts.suggestions} icon={LightBulbIcon} />
    <EventsTabButton label="Completed" tabKey="completed" activeTab={activeTab} onClick={() => navigate("/events/completed")} count={counts.completed} icon={CheckCircleIcon} />
    <EventsTabButton label="Cancelled" tabKey="cancelled" activeTab={activeTab} onClick={() => navigate("/events/cancelled")} count={counts.cancelled} icon={XCircleIcon} />
    {isSuperAdmin && (
      <EventsTabButton label="Hosts" tabKey="hosts" activeTab={activeTab} onClick={() => navigate("/events/hosts")} count={counts.hosts} icon={UserGroupIcon} />
    )}
    {isSuperAdmin && (
      <EventsTabButton label="Requests" tabKey="requests" activeTab={activeTab} onClick={() => navigate("/events/requests")} count={counts.requests} icon={InboxArrowDownIcon} />
    )}
  </div>
);
